import { useState, useCallback } from "react";

// Declare grecaptcha on window for TypeScript
declare global {
    interface Window {
        grecaptcha?: {
            render?: () => void;
        };
    }
}

// Utility function to wait for a condition with retries
const waitForCondition = async (conditionFn: () => boolean, maxRetries: number, interval: number): Promise<void> => {
    for (let retryCount = 0; retryCount < maxRetries; retryCount++) {
        if (conditionFn()) return;
        await new Promise((resolve) => setTimeout(resolve, interval));
    }
    throw new Error("Something went wrong. Please reload your page and try again.");
};

const useRecaptchaLoader = () => {
    const [isRecaptchaLoaded, setIsRecaptchaLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string>("");

    const maxRetries = 70; // 7 seconds
    const retryInterval = 100;  // 100ms

    const isRecaptchaAvailable = () => !!window.grecaptcha?.render;

    // Callback triggered when reCAPTCHA script is loaded
    const asyncScriptOnLoad = useCallback(() => {
        setIsRecaptchaLoaded(true);
        setIsLoading(false);
    }, []);


    // Function to manually trigger reCAPTCHA loading check
    const waitForRecaptcha = useCallback(async () => {
        setIsLoading(true);
        setError("");
        try {
            await waitForCondition(isRecaptchaAvailable, maxRetries, retryInterval);
            setIsRecaptchaLoaded(true);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "An unknown error occurred.";
            console.error(errorMessage);
            setError("Something went wrong. Please reload your page and try again.");
        } finally {
            setIsLoading(false);
        }
    }, []);

    return { isRecaptchaLoaded, isLoading, error, waitForRecaptcha, asyncScriptOnLoad };
};

export default useRecaptchaLoader;
